export const CONTACT_POSITIONS = ['header', 'detail', 'passcode'] as const;
export type AnalyticsKind = 'pv' | 'detail' | 'contact' | 'search';
export interface AnalyticsEvent {
  id: string;
  visitor: string;
  visit: string;
  kind: AnalyticsKind;
  channel?: string;
  topicId?: string;
  position?: (typeof CONTACT_POSITIONS)[number];
  term?: string;
  noResults?: boolean;
}
// Match recognizable phone structures, not arbitrary digits joined across a
// normal search (dates, ages and short lesson numbers remain searchable).
const PHONE_SEPARATOR = '[\\s\\-‐‑‒–—().]*';
const MOBILE_PHONE = new RegExp(
  `(?:^|[^\\d])1${PHONE_SEPARATOR}[3-9](?:${PHONE_SEPARATOR}\\d){9}(?!\\d)`,
);
const INTERNATIONAL_PHONE = new RegExp(
  `(?:\\+|\\b00)[1-9](?:${PHONE_SEPARATOR}\\d){7,14}(?!${PHONE_SEPARATOR}\\d)`,
);
const NORTH_AMERICAN_PHONE =
  /(?:^|[^\d])\(?[2-9]\d{2}\)?[\s\-‐‑‒–—]+\d{3}[\s\-‐‑‒–—]+\d{4}(?!\d)/;
export function normalizeSearchTerm(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const term = value
    .normalize('NFKC')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  if (
    !term ||
    term.length > 80 ||
    MOBILE_PHONE.test(term) ||
    INTERNATIONAL_PHONE.test(term) ||
    NORTH_AMERICAN_PHONE.test(term) ||
    /\d{6,}|@|https?:|www\.|[\w-]+\.[a-z]{2,}(?:\b|\/)|[\u0000-\u001f\u007f]/i.test(
      term,
    )
  )
    return null;
  return term;
}
export function beijingDay(now = new Date()): string {
  return new Date(now.getTime() + 8 * 3600000).toISOString().slice(0, 10);
}
export function addDays(day: string, days: number): string {
  return new Date(Date.parse(day + 'T00:00:00Z') + days * 86400000)
    .toISOString()
    .slice(0, 10);
}
export function mondayOf(day: string): string {
  const weekday = new Date(day + 'T00:00:00Z').getUTCDay();
  return addDays(day, -((weekday + 6) % 7));
}
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function parseAnalyticsEvent(input: unknown): AnalyticsEvent {
  if (!input || typeof input !== 'object' || Array.isArray(input))
    throw new Error('无效事件');
  const e = input as Record<string, unknown>;
  const common = ['id', 'visitor', 'visit', 'kind', 'channel'];
  const extras: Record<string, string[]> = {
    pv: [],
    detail: ['topicId'],
    contact: ['position'],
    search: ['term', 'noResults'],
  };
  if (
    typeof e.kind !== 'string' ||
    !Object.prototype.hasOwnProperty.call(extras, e.kind)
  )
    throw new Error('无效事件');
  if (
    Object.keys(e).some(
      (key) => ![...common, ...extras[e.kind as string]].includes(key),
    )
  )
    throw new Error('无效字段');
  for (const key of ['id', 'visitor', 'visit'])
    if (typeof e[key] !== 'string' || !UUID.test(e[key] as string))
      throw new Error('无效编号');
  if (
    e.channel !== undefined &&
    (typeof e.channel !== 'string' || !/^[a-zA-Z0-9_-]{1,64}$/.test(e.channel))
  )
    throw new Error('无效渠道格式');
  if (
    e.kind === 'detail' &&
    (typeof e.topicId !== 'string' || !UUID.test(e.topicId))
  )
    throw new Error('无效主题');
  if (e.kind === 'contact' && !CONTACT_POSITIONS.includes(e.position as any))
    throw new Error('无效入口');
  if (e.kind === 'search') {
    const term = normalizeSearchTerm(e.term);
    if (!term || typeof e.noResults !== 'boolean') throw new Error('无效搜索');
    return { ...e, term } as unknown as AnalyticsEvent;
  }
  return e as unknown as AnalyticsEvent;
}
export function classifyRejection(
  reason: string | null,
): 'passcode' | 'quota' | 'resource' | 'unknown' {
  if (['兑换码无效', '账号已冻结', '会员已过期'].includes(reason || ''))
    return 'passcode';
  if (['超出月配额', '超出单日上限'].includes(reason || '')) return 'quota';
  if (['资源不存在', '领取资源内容为空'].includes(reason || ''))
    return 'resource';
  return 'unknown';
}
export interface AnalyticsMetric {
  event: AnalyticsKind;
  hits: number;
  visitors: number;
  no_results: number;
}
export interface AnalyticsChannel {
  id: string;
  name: string;
  note: string;
  code: string;
  enabled: boolean;
  created_at: string;
  metrics: AnalyticsMetric[];
}
export interface AnalyticsSummary {
  from: string;
  to: string;
  collecting: boolean;
  overview: { pv: number; uv: number };
  trend: Array<{ bucket: string; pv: number; uv: number }>;
  links: AnalyticsChannel[];
  channelGroups: Array<{ name: string; metrics: AnalyticsMetric[] }>;
  specialChannels: Array<{ state: string; metrics: AnalyticsMetric[] }>;
  topics: Array<{
    id: string;
    name: string;
    detail_hits: number;
    detail_uv: number;
    historical_views: number;
    successful_claims: number;
  }>;
  searches: Array<{
    term: string;
    hits: number;
    visitors: number;
    no_results: number;
  }>;
  noResultSearches: AnalyticsSummary['searches'];
  contacts: Array<{ position: string; hits: number; visitors: number }>;
  redemptions: {
    first_success: number;
    unmatched_topic: number;
    weekly_members: Array<{ week: string; members: number }>;
    rejected: Record<'passcode' | 'quota' | 'resource' | 'unknown', number>;
  };
}
